import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { Button, Modal } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { debounce } from 'lodash'

import InternshipsTable from '../../Components/shared/InternshipsManager/InternshipsTable'
import InternshipsHeader, { PRECISION_YEAR } from '../../Components/shared/InternshipsManager/InternshipsHeader'
import { Contexts, DATE_WITHOUT_TIME, InternshipStates } from '../../utils/constants'
import { selectUser } from '../../reducers/UserReducer'
import InternshipsManagerProvider from '../../Context/InternshipsManager/InternshipsManagerProvider'
import StudentInfo from '../../Components/shared/StudentInfo'
import InternshipInfo from '../../Components/shared/InternshipInfo'
import { deleteInternship, getAllInternships } from '../../utils/api/internship'
import { onError, onSuccess, purgeMetadata } from '../../utils/apiHelper'
import InternshipValidation from '../../Components/shared/ShiftsManager/InternshipValidation'
import { getState } from '../../reducers/InternshipsManagerReducer/actions'
import { INTERNSHIPS_MANAGER } from '../../reducers/InternshipsManagerReducer/actionsType'
import { getInternshipsQuotas } from '../../utils/api/quota'
import InternshipRejection from '../../Components/shared/InternshipsManager/InternshipRejection'
import useLocalStorageOld from '../../hooks/UseLocalStorage'
import { DEFAULT_INTERNSHIP_FILTERS, GlobalFiltersContext } from '../../Providers/GlobalFiltersProvider'
import MessageBox from '../../Components/shared/MessageBox'
import { InternshipMessageContext } from '../../Providers/InternshipMessageProvider'
import LOCAL_STORAGE_KEYS from '../../utils/localStorage'
import { selectTranslate } from '../../reducers/LocaleReducer/LocalReducer'
import { GlobalContext } from '../../Providers/GlobalProvider'
import InternshipToolBar from '../school/InternshipsView/InternshipToolBar'
import { SelectContext } from '../../Providers/SelectProvider'
import useInternshipEntityFilters from '../../hooks/useInternshipEntityFilters'

const { PRECISION } = LOCAL_STORAGE_KEYS.INTERNSHIPS_MANAGER

const DEFAULT_META_DATA = {
  page: 1,
  last: 0,
  perPage: 10,
  totalPages: 1,
  total: 0
}
const TIME_BEFORE_SEARCH = 200
const INTERNSHIPS_NUMBER = 10

const InternshipsManagerView = ({ loadShiftsManager }) => {
  const { isDraft } = useContext(GlobalContext)
  const { trackedInternships, setTrackedInternships } = useContext(InternshipMessageContext)
  const { internshipDate, internshipFilters, internshipSearch, setInternshipFilters, setInternshipSearch } = useContext(GlobalFiltersContext)
  const { selected, setSelected } = useContext(SelectContext)
  const { sectionToYears, sectors } = useInternshipEntityFilters()

  const refusedInternship = useSelector(state => getState(state).refusedInternship)
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [displayEarlyModal, setDisplayEarlyModal] = useState(false)
  const [internshipToValidate, setInternshipToValidate] = useState(null)
  const [internships, setInternships] = useState([])
  const [metadata, setMetadata] = useState(DEFAULT_META_DATA)
  const [page, setPage] = useState(1)
  const [quotas, setQuotas] = useState({})
  const [selectedInternship, setSelectedInternship] = useState(null)
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [loading, setLoading] = useState(false)

  const [precision, setPrecision] = useLocalStorageOld(PRECISION, PRECISION_YEAR)

  const dispatch = useDispatch()

  const period = useMemo(() => ({
    startDate: internshipDate.format(DATE_WITHOUT_TIME),
    endDate: internshipDate.clone().add(internshipFilters.weeksShownCount - 1, 'week').endOf('isoWeek').format(DATE_WITHOUT_TIME)
  }), [internshipDate.format(DATE_WITHOUT_TIME), internshipFilters.weeksShownCount])

  const parameters = useMemo(() => ({
    order: internshipFilters.order,
    page,
    perPage: INTERNSHIPS_NUMBER,
    period: period,
    search: internshipSearch,
    filters: {
      schools: internshipFilters.schools,
      institutions: internshipFilters.institutions,
      sectionToYears,
      sectors,
      states: internshipFilters.states,
      areStartedExcluded: internshipFilters.areStartedExcluded,
      ownership: internshipFilters.ownership,
      isDraft
    }
  }), [internshipFilters, internshipSearch, isDraft, page, period, sectionToYears, sectors])

  const fetchInternships = useCallback(() => {
    setLoading(true)

    const urlParameters = { ...parameters }

    if (user.context === Contexts.SCHOOL) {
      urlParameters.contexts = [Contexts.SCHOOL.toLowerCase()]
    }

    getAllInternships(user, urlParameters).then(json => {
      if (json?.data) {
        setInternships(json.data)
        setTrackedInternships(json.data.map(i => i.id))
      }

      if (json?.meta) {
        setMetadata(purgeMetadata(json.meta.pages))
      }

      setLoading(false)
    })
  }, [user, parameters])

  const fetchQuotas = useCallback(() => {
    if (trackedInternships.length > 0) {
      getInternshipsQuotas(user, trackedInternships, { precision, period }).then(json => {
        if (json?.data) {
          setQuotas(json.data)
        }
      })
    }
  }, [period, precision, trackedInternships, user])

  useEffect(() => setSelected([]), [page, isDraft])
  useEffect(() => {
    setPage(1)
    setSelected([])
  }, [internshipDate.format(DATE_WITHOUT_TIME), internshipSearch])

  useEffect(() => {
    const handler = debounce(() => fetchInternships(), TIME_BEFORE_SEARCH)

    handler()

    return () => {
      handler.cancel()
    }
  }, [fetchInternships])

  useEffect(() => {
    const handler = debounce(() => fetchQuotas(), TIME_BEFORE_SEARCH)

    handler()

    return () => {
      handler.cancel()
    }
  }, [fetchQuotas])

  const handleInternshipDeletion = internship => {
    deleteInternship(user, internship).then(() => {
      onSuccess(t('internships_manager.delete_internship_success'))
      fetchInternships()
    }).catch(error => {
      onError(t('internships_manager.delete_internship.' + error.status))
    })
  }

  const handleInternshipValidation = () => {
    const updatedInternships = internships.map(internship => {
      if (internship.id === internshipToValidate.id) {
        return { ...internship, state: InternshipStates.SCHEDULE_VALIDATED }
      }

      return internship
    })

    setInternships(updatedInternships)
    setInternshipToValidate({ ...internshipToValidate, state: InternshipStates.SCHEDULE_VALIDATED })
  }

  const handleInternshipInvalidation = newState => {
    const updatedInternships = internships.map(internship => {
      if (internship.id === internshipToValidate.id) {
        return { ...internship, state: newState }
      }

      return internship
    })

    setInternships(updatedInternships)
    setInternshipToValidate({ ...internshipToValidate, state: newState })
  }

  const handleRejectionClosing = () => {
    dispatch({
      type: INTERNSHIPS_MANAGER.SET_REFUSED_INTERNSHIP,
      payload: null
    })
  }

  const handleFilterChange = () => {
    setSelected([])
    setPage(1)
  }

  const handleResetFilters = () => {
    setSelected([])
    setPage(1)
    setInternshipSearch('')
    setPrecision(PRECISION_YEAR)
    setInternshipFilters(DEFAULT_INTERNSHIP_FILTERS)
  }

  const handleUnselect = reload => {
    setSelected([])

    if (reload) {
      fetchInternships()
    }
  }

  return (
    <>
      <InternshipsHeader
        page={page}
        precision={precision}
        metadata={metadata}
        loadShiftsManager={loadShiftsManager}
        onPageChange={setPage}
        onPrecisionChange={setPrecision}
        onFilterChange={handleFilterChange}
        onResetFilters={handleResetFilters}
        refresh={fetchInternships}
      />
      <InternshipsManagerProvider
        internships={internships}
        quotas={quotas}
        weeksShownCount={internshipFilters.weeksShownCount}
        onEarlyStartDate={setDisplayEarlyModal}
        onInternshipDelete={handleInternshipDeletion}
        onInternshipSelected={setSelectedInternship}
        onStudentSelected={setSelectedStudent}
        refreshInternships={fetchInternships}
        refreshQuotas={fetchQuotas}
        setInternshipToValidate={setInternshipToValidate}
      >
        <InternshipsTable loading={loading} />
      </InternshipsManagerProvider>
      <StudentInfo
        student={selectedStudent}
        onClose={() => setSelectedStudent(null)}
      />
      <InternshipInfo
        internship={selectedInternship}
        onClose={() => setSelectedInternship(null)}
      />
      {internshipToValidate && (
        <InternshipValidation
          internship={internshipToValidate}
          onClose={() => setInternshipToValidate(null)}
          onValidate={handleInternshipValidation}
          onInvalidate={handleInternshipInvalidation}
        />
      )}
      <InternshipRejection
        internship={refusedInternship}
        onClose={handleRejectionClosing}
      />
      <Modal
        title={t('Start date does not observe waiting period')}
        visible={displayEarlyModal}
        footer={<Button type='primary' onClick={() => setDisplayEarlyModal(false)}> {t('common.ok')} </Button>}
      >
        {t('alert.late.internship.request')}
      </Modal>
      <MessageBox />
      {selected.length > 0 && <InternshipToolBar internships={selected} isDraft={isDraft} onUnselect={handleUnselect} />}
    </>
  )
}

export default InternshipsManagerView
